import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Package } from 'lucide-react';
import { fetchOrderById, updateOrderStatus } from '../services/api';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    try {
      const data = await fetchOrderById(id);
      setOrder(data);
    } catch (error) {
      console.error('Failed to load order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateOrderStatus(id, newStatus);
      setOrder({ ...order, status: newStatus });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading order details...</div>;
  }

  if (!order) {
    return <div className="text-center py-12">Order not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/orders')} className="btn-secondary">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-3xl font-bold">Order Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Order #{order.order_number}</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/orders/${id}/edit`)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Edit size={16} />
                  Edit
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Customer:</span>
                <Link to={`/customers/${order.customer_id}`} className="text-primary-600 hover:underline">
                  {order.customer_name}
                </Link>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Order Date:</span>
                <span className="font-medium">{new Date(order.order_date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <select
                  value={order.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="px-3 py-1 border rounded"
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Order Items</h3>
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Product</th>
                  <th className="text-right py-2">Quantity</th>
                  <th className="text-right py-2">Price</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-3">{item.product_name}</td>
                    <td className="text-right">{item.quantity}</td>
                    <td className="text-right">${Number(item.unit_price || 0).toFixed(2)}</td>
                    <td className="text-right">${Number((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td colSpan="3" className="text-right py-3">Total:</td>
                  <td className="text-right py-3">${Number(order.total_amount || 0).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Shipping Info */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Shipping Address</h3>
            <div className="text-gray-700 space-y-1">
              <p>{order.shipping_address?.street}</p>
              <p>{order.shipping_address?.city}, {order.shipping_address?.state}</p>
              <p>{order.shipping_address?.zip_code}</p>
              <p>{order.shipping_address?.country}</p>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Tracking Info</h3>
            {order.tracking_number ? (
              <div>
                <p className="text-sm text-gray-600">Tracking Number</p>
                <p className="font-mono text-sm mt-1">{order.tracking_number}</p>
              </div>
            ) : (
              <p className="text-gray-500">No tracking information available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
